import React, { useState } from "react";
import { ArrowRight, MessageCircle, Check } from "lucide-react";
import { useHome } from "@/i18n/home";
import { CONTACT } from "@/i18n/translations";
import { VillaImage } from "./VillaImage";

const LAYERS = [["wifi", "#00C3FF", "legend_ap"], ["cams", "#FF7A00", "legend_cam"], ["sat", "#FFC93C", "legend_sat"]];

const Layers = ({ layers, flip, v }) => (
  <div className="mb-4 flex flex-wrap gap-2" data-testid="villa-layers">
    {LAYERS.map(([k, c, label]) => (
      <button key={k} onClick={() => flip(k)} aria-pressed={layers[k]} data-testid={`layer-${k}`}
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${layers[k] ? "border-[#090A0C] bg-[#090A0C] text-white" : "border-[#E5E7EB] bg-white text-[#6B7280] hover:text-[#090A0C]"}`}>
        <span className="grid h-4 w-4 place-items-center rounded-full" style={{ background: layers[k] ? c : "transparent", border: `2px solid ${c}` }}>
          {layers[k] && <Check size={10} strokeWidth={3.5} color="#090A0C" />}
        </span>
        {v[label]}
      </button>
    ))}
  </div>
);

const Toggle = ({ on, setOn, v }) => (
  <div className="inline-flex rounded-full border border-[#E5E7EB] bg-white p-1" data-testid="villa-toggle">
    {[{ val: false, id: "off", label: v.without }, { val: true, id: "on", label: v.with }].map((it) => (
      <button
        key={it.id}
        onClick={() => setOn(it.val)}
        data-testid={`villa-toggle-${it.id}`}
        className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${on === it.val ? (it.val ? "bg-[#090A0C] text-white" : "bg-[#6B7280] text-white") : "text-[#4B5563] hover:text-[#090A0C]"}`}
      >
        {it.label}
      </button>
    ))}
  </div>
);

export const VillaCoverage = () => {
  const v = useHome().villa;
  const [on, setOn] = useState(true);
  const [active, setActive] = useState(null);
  const [layers, setLayers] = useState(() => {
    const small = window.matchMedia("(max-width: 640px)").matches;
    return { wifi: true, cams: !small, sat: !small };
  });
  const flip = (k) => { setLayers((l) => ({ ...l, [k]: !l[k] })); setActive(null); };
  const house = "chalet";
  const toggle = (val) => { setOn(val); setActive(null); };

  return (
    <section id="coverage" className="relative bg-white py-24 sm:py-32" data-testid="villa-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0B87B0]">{v.overline}</p>
            <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-[#090A0C] sm:text-5xl">{v.title}</h2>
            <p className="mt-5 text-lg leading-relaxed text-[#4B5563]">{v.lead}</p>
          </div>
          <Toggle on={on} setOn={toggle} v={v} />
        </div>

        <div className="mt-12">
          <div className={`transition-opacity duration-300 ${on ? "opacity-100" : "pointer-events-none opacity-0"}`} aria-hidden={!on}>
            <Layers layers={layers} flip={flip} v={v} />
          </div>
          <VillaImage house={house} on={on} active={active} onSelect={setActive} labels={v} names={house === "chalet" ? v.aps_chalet : v.aps} layers={layers} />
          <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-[#6B7280]">{v.hint}</p>
            <a href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(v.wa_text)}`} target="_blank" rel="noopener noreferrer" data-testid="villa-cta" className="group inline-flex w-fit items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(37,211,102,0.35)] transition-transform hover:scale-[1.03]">
              <MessageCircle size={17} /> {v.want} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
